// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE
// version  : 1.0
// updated  : 2026-06-15
// replaces : -
// protected: NO  -> Tambahkan riwayat di /app/memory CHANGELOG saat diubah.
// =============================================================================

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const RealtimeContext = createContext(null);
export const useRealtime = () => useContext(RealtimeContext);

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState("offline"); // 'live' | 'connecting' | 'offline'
  const [lastEvent, setLastEvent] = useState(null);

  const wsRef = useRef(null);
  const listenersRef = useRef(new Set()); // each entry: { scopes: string[]|null, fn }
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const attemptRef = useRef(0);
  const closedByUs = useRef(false);

  // Subscribe a listener. scopes=null/[] means "all events".
  const subscribe = useCallback((scopes, fn) => {
    const entry = { scopes: scopes && scopes.length ? scopes : null, fn };
    listenersRef.current.add(entry);
    return () => listenersRef.current.delete(entry);
  }, []);

  useEffect(() => {
    closedByUs.current = false;

    const clearTimers = () => {
      if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
      if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }
    };

    const scheduleReconnect = () => {
      if (closedByUs.current) return;
      attemptRef.current += 1;
      const delay = Math.min(1000 * 2 ** Math.min(attemptRef.current, 4), 15000); // 2s..15s
      reconnectTimer.current = setTimeout(connect, delay);
    };

    const connect = () => {
      if (!user) { setStatus("offline"); return; }
      setStatus("connecting");
      let ws;
      try {
        // Auth via the httpOnly cookie (sent automatically on same-origin WS handshake).
        ws = new WebSocket(`${WS_BASE}/api/ws`);
      } catch (e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setStatus("live");
        if (pingTimer.current) clearInterval(pingTimer.current);
        pingTimer.current = setInterval(() => {
          try { ws.readyState === 1 && ws.send("ping"); }
          catch (e) { console.debug("WS ping failed:", e?.message || e); }
        }, 25000);
      };

      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); }
        catch (err) { console.debug("WS message parse error:", err?.message || err); return; }
        if (msg.event !== "data_changed") return;
        setLastEvent(msg);
        const evScopes = msg.scopes || [];
        listenersRef.current.forEach((l) => {
          if (!l.scopes || l.scopes.some((s) => evScopes.includes(s))) {
            try { l.fn(msg); }
            catch (err) { console.error("Realtime listener error:", err?.message || err); }
          }
        });
      };

      ws.onerror = () => { try { ws.close(); } catch (e) { console.debug("WS close on error ignored:", e?.message || e); } };
      ws.onclose = () => {
        if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }
        setStatus("offline");
        scheduleReconnect();
      };
    };

    if (user) {
      connect();
    } else {
      setStatus("offline");
    }

    return () => {
      closedByUs.current = true;
      clearTimers();
      try { wsRef.current && wsRef.current.close(); }
      catch (e) { console.debug("WS cleanup close ignored:", e?.message || e); }
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  return (
    <RealtimeContext.Provider value={{ status, lastEvent, subscribe }}>
      {children}
    </RealtimeContext.Provider>
  );
};

/**
 * useLiveRefresh — call `fn` whenever a realtime event with matching scopes arrives.
 * @param {string[]} scopes  e.g. ["inventory", "transfers"]. Empty/null = all events.
 * @param {Function} fn       reload callback (kept fresh via ref).
 */
export function useLiveRefresh(scopes, fn) {
  const ctx = useRealtime();
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const key = JSON.stringify(scopes || []);
  useEffect(() => {
    if (!ctx || !ctx.subscribe) return undefined;
    return ctx.subscribe(scopes, (msg) => { if (fnRef.current) fnRef.current(msg); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx && ctx.subscribe, key]);
}
